import {
  Checkbox,
  Grid,
  InfoIcon,
  Text,
  TextSize,
  TextWeight,
  makeStyles
} from '@focaldata/cin-ui-components'
import TextField from '@mui/material/TextField'
import { useEffect, useMemo, useRef, useState } from 'react'
import { Navigate } from 'react-router-dom'
import ErrorPage, { ErrorType } from '../../../controls/ErrorPage'
import { ProjectStatus, Wave } from '../../../data/gql-gen/dashboard/graphql'
import useGetSurveysProgress from '../../../hooks/useGetSurveysProgress'
import { useProjectId } from '../../../hooks/useProjectId'
import { formatThousandsWithCommas } from '../../../utils/HelperFunctions'
import dayjs from '../../../utils/dayjs'
import { WaveFlowBreadcrumb } from './WaveFlowBreadcrumb'
import { useSetWaveExclusions, useWaveExclusions } from './useWaveExclusions'
import { useWaveId } from './useWaveId'
import { useWaves } from './useWaves'

export const EXCLUSION_WINDOW_NOTE =
  'PureSpectrum only excludes respondents who completed a selected wave in the last 365 days. People from waves that finished more than a year ago can take part again.'

const useStyles = makeStyles()({
  page: { padding: '24px 32px' },
  intro: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    marginTop: 8,
    maxWidth: 640,
    color: '#42526E'
  },
  panel: {
    border: '1px solid #E5EAF5',
    borderRadius: 12,
    marginTop: 20,
    background: '#FFFFFF',
    maxWidth: 820,
    overflow: 'hidden'
  },
  toolbar: {
    display: 'flex',
    alignItems: 'center',
    gap: 16,
    padding: '12px 16px',
    borderBottom: '1px solid #E5EAF5'
  },
  search: { flex: 1, maxWidth: 280 },
  // reset so a native <button> can be styled as a link action
  linkButton: {
    background: 'none',
    border: 'none',
    padding: 0,
    font: 'inherit',
    cursor: 'pointer',
    color: 'var(--primary)',
    fontWeight: 600
  },
  count: { marginLeft: 'auto', color: '#6B7280', whiteSpace: 'nowrap' },
  // Capped so a 100-wave tracker doesn't push the toolbar off-screen.
  list: { maxHeight: 'calc(100vh - 360px)', overflowY: 'auto' },
  row: {
    display: 'grid',
    gridTemplateColumns: '1fr auto',
    alignItems: 'center',
    gap: 16,
    padding: '12px 16px',
    borderBottom: '1px solid #F1F3F7'
  },
  waveMeta: { marginTop: 2, color: '#6B7280' },
  excluded: { color: '#00875A', fontWeight: 600, whiteSpace: 'nowrap' },
  empty: { padding: '20px 16px', color: '#6B7280' },
  hint: { marginTop: 12, color: '#6B7280' },
  error: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    marginTop: 12,
    color: '#DE350B'
  }
})

// Keyed by wave, so moving between waves on this route starts from that wave's own selection.
export const WaveDedupPage = () => {
  const waveId = useWaveId()
  return <WaveDedup key={waveId} />
}

/** Only earlier waves can be de-duped against, most recent first. */
const WaveDedup = () => {
  const { classes } = useStyles()
  const projectId = useProjectId()
  const waveId = useWaveId()
  const { waves, loading, refetch: refetchWaves } = useWaves(projectId)
  const {
    excludedWaveIds: savedExcludedWaveIds,
    loading: exclusionsLoading,
    error: exclusionsError
  } = useWaveExclusions(projectId, waveId)
  const { setWaveExclusions } = useSetWaveExclusions(projectId)
  const wave = waves.find((candidate) => candidate.waveId === waveId)

  const earlierWaves = useMemo(
    () =>
      wave
        ? waves
            .filter((candidate) => candidate.number < wave.number)
            .sort((a, b) => b.number - a.number)
        : [],
    [waves, wave]
  )

  const earlierSurveyIds = earlierWaves.flatMap((earlierWave) =>
    earlierWave.surveys.map((survey) => survey.surveyId)
  )
  const { surveyProgress, loading: progressLoading } = useGetSurveysProgress(
    earlierSurveyIds.length > 0 ? earlierSurveyIds : undefined
  )
  const completesBySurveyId = new Map(
    (surveyProgress ?? []).map((survey) => [
      survey.surveyId,
      survey.progress?.samplesCollected ?? 0
    ])
  )

  const [excludedWaveIds, setExcludedWaveIds] = useState<Set<string> | null>(
    null
  )
  // null until touched, so an untouched wave shows what is saved. There is no
  // default yet: an untouched wave comes back empty.
  const selected = excludedWaveIds ?? new Set(savedExcludedWaveIds)
  // Only earlier waves are valid; the backend rejects anything else.
  const excluded = new Set(
    earlierWaves
      .filter((earlierWave) => selected.has(earlierWave.waveId))
      .map((earlierWave) => earlierWave.waveId)
  )

  const [saveFailed, setSaveFailed] = useState(false)
  const mounted = useRef(true)
  useEffect(() => {
    mounted.current = true
    return () => {
      mounted.current = false
    }
  }, [])

  const save = async (selection: Set<string>) => {
    if (!wave) {
      return
    }
    try {
      const saved = await setWaveExclusions(wave.waveId, [...selection])
      // Null means a newer selection is queued behind this one and reports for it.
      if (saved && mounted.current) {
        setSaveFailed(false)
      }
    } catch {
      if (mounted.current) {
        setSaveFailed(true)
      }
      // A refused save usually means the wave has since started launching.
      void refetchWaves()
    }
  }

  const select = (selection: Set<string>) => {
    setExcludedWaveIds(selection)
    void save(selection)
  }

  const [search, setSearch] = useState('')
  const visibleWaves = search
    ? earlierWaves.filter((earlierWave) =>
        earlierWave.label.toLowerCase().includes(search.toLowerCase())
      )
    : earlierWaves

  const toggle = (toggledWaveId: string) => {
    const next = new Set(excluded)
    if (next.has(toggledWaveId)) {
      next.delete(toggledWaveId)
    } else {
      next.add(toggledWaveId)
    }
    select(next)
  }

  if (loading || exclusionsLoading) {
    return (
      <div>
        <WaveFlowBreadcrumb activeStep="De-dup" />
        <div className={classes.page}>
          <Text size={TextSize.ms}>Loading…</Text>
        </div>
      </div>
    )
  }

  if (!wave) {
    return <Navigate to={`/project/${projectId}`} replace />
  }

  // Without the saved selection, a save would overwrite it with a guess.
  if (exclusionsError) {
    return <ErrorPage errorType={ErrorType.GenericError} />
  }

  const excludedCount = excluded.size
  // The backend refuses changes once any market has left draft.
  const locked = wave.status !== ProjectStatus.Draft

  return (
    <div>
      <WaveFlowBreadcrumb activeStep="De-dup" />

      <div className={classes.page}>
        <Text size={TextSize.xl} weight={TextWeight.Bold}>
          De-duplication
        </Text>
        <div className={classes.intro}>
          <Text size={TextSize.ms}>
            Exclude respondents who took part in earlier waves, market by
            market. Select the waves
            {` ${wave.label}`} should be de-duped against.
          </Text>
          <InfoIcon
            text={EXCLUSION_WINDOW_NOTE}
            aria-label={EXCLUSION_WINDOW_NOTE}
          />
        </div>

        {earlierWaves.length === 0 ? (
          <div className={classes.panel}>
            <div className={classes.empty}>
              <Text size={TextSize.ms}>
                {wave.label} is the first wave, so there are no earlier waves to
                de-duplicate against.
              </Text>
            </div>
          </div>
        ) : (
          <div className={classes.panel}>
            <div className={classes.toolbar}>
              <div className={classes.search}>
                <TextField
                  fullWidth
                  size="small"
                  placeholder="Search waves"
                  value={search}
                  onChange={(event) => setSearch(event.target.value)}
                  inputProps={{ 'aria-label': 'Search waves' }}
                />
              </div>
              {!locked && (
                <>
                  <button
                    type="button"
                    className={classes.linkButton}
                    onClick={() =>
                      select(
                        new Set(earlierWaves.map((earlier) => earlier.waveId))
                      )
                    }
                  >
                    <Text size={TextSize.ms}>Exclude all</Text>
                  </button>
                  <button
                    type="button"
                    className={classes.linkButton}
                    onClick={() => select(new Set())}
                  >
                    <Text size={TextSize.ms}>Clear</Text>
                  </button>
                </>
              )}
              <span className={classes.count}>
                <Text size={TextSize.ms}>
                  {`${excludedCount} of ${earlierWaves.length} excluded`}
                </Text>
              </span>
            </div>

            <div className={classes.list}>
              {visibleWaves.length === 0 ? (
                <div className={classes.empty}>
                  <Text size={TextSize.ms}>No waves match “{search}”.</Text>
                </div>
              ) : (
                visibleWaves.map((earlierWave) => (
                  <WaveRow
                    key={earlierWave.waveId}
                    wave={earlierWave}
                    completes={
                      !progressLoading &&
                      earlierWave.surveys.every((survey) =>
                        completesBySurveyId.has(survey.surveyId)
                      )
                        ? earlierWave.surveys.reduce(
                            (sum, survey) =>
                              sum +
                              (completesBySurveyId.get(survey.surveyId) ?? 0),
                            0
                          )
                        : undefined
                    }
                    currentWaveLabel={wave.label}
                    excluded={excluded.has(earlierWave.waveId)}
                    disabled={locked}
                    onToggle={() => toggle(earlierWave.waveId)}
                  />
                ))
              )}
            </div>
          </div>
        )}

        {locked && (
          <div className={classes.hint}>
            <Text size={TextSize.s}>
              {`${wave.label} has started launching, so the waves it’s de-duped against can no longer change.`}
            </Text>
          </div>
        )}

        {saveFailed && (
          <div className={classes.error}>
            <Text size={TextSize.ms}>De-duplication couldn’t be applied.</Text>
            <button
              type="button"
              className={classes.linkButton}
              onClick={() => void save(excluded)}
            >
              <Text size={TextSize.ms}>Retry</Text>
            </button>
          </div>
        )}
      </div>
    </div>
  )
}

const WaveRow = ({
  wave,
  completes,
  currentWaveLabel,
  excluded,
  disabled,
  onToggle
}: {
  wave: Wave
  completes: number | undefined
  currentWaveLabel: string
  excluded: boolean
  disabled: boolean
  onToggle: () => void
}) => {
  const { classes } = useStyles()

  return (
    <div className={classes.row}>
      <Grid container direction="row" alignItems="center">
        <Checkbox
          checked={excluded}
          disabled={disabled}
          onChange={onToggle}
          ariaLabel={`Exclude ${wave.label}`}
          label={
            <span>
              <Text size={TextSize.ms} weight={TextWeight.SemiBold}>
                {wave.label}
              </Text>
              <span className={classes.waveMeta}>
                <Text size={TextSize.s}>
                  {dayjs(wave.createdDate).format('MMM YYYY')}
                  {completes === undefined
                    ? ''
                    : ` · ${formatThousandsWithCommas(completes)} completes`}
                </Text>
              </span>
            </span>
          }
        />
      </Grid>
      {excluded && (
        <span className={classes.excluded}>
          <Text size={TextSize.ms}>{`Excluded from ${currentWaveLabel}`}</Text>
        </span>
      )}
    </div>
  )
}
